import React, { useRef, useState, useEffect, useMemo } from "react";
import "emoji-mart/css/emoji-mart.css";
import { Picker } from "emoji-mart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSmile } from "@fortawesome/free-solid-svg-icons";
import {
    faMicrophone,
    faCheck,
    faTimes,
    faHourglassStart,
} from "@fortawesome/free-solid-svg-icons";
import CTE from "../Constants";
import { isMobileView } from "frontend-lib/Utils/Mobile";
import { useAppContext } from "State/Global/AppContext";
import { IonProgressBar } from "@ionic/react";
import { t } from "i18next";

let counterInterval;
const defaultTextInputHeight = 36;

function WrapInProgress({ showProgess, children }) {
    if (showProgess) {
        return (
            <div style={{ width: "100%" }}>
                <IonProgressBar type="indeterminate" />
                <div style={{ display: "flex", width: "100%" }}>{children}</div>
            </div>
        );
    }
    return <>{children}</>;
}

function SendMessages(props) {
    const textInput = useRef(null);
    const fileInput = useRef(null);
    const [textInputHeight, setTextInputHeight] = useState(defaultTextInputHeight);
    const [showEmojis, setShowEmojis] = useState(false);
    const [recordCounter, setRecordCounter] = useState(0);
    const emojiCategories = ["flags", "symbols", "places", "recent", "foods"];

    const { appState } = useAppContext();

    const inputPlaceholder = useMemo(() => {
        if (
            appState.notificationSocketStatus === "CONNECTED" &&
            appState.chatSocketStatus === "CONNECTED"
        ) {
            return { placeholder: props.inputPlaceholder, status: "ok" };
        }
        return { placeholder: t("chatPlaceholders.reconnecting"), status: "err" };
    }, [appState.notificationSocketStatus, appState.chatSocketStatus, props.inputPlaceholder]);

    function handleTextInput(data) {
        props.handleTextInputCb(data);
    }

    function handleInputKeyPress(event) {
        const scrollHeight = textInput.current.scrollHeight;
        const hasScrollBar = textInput.current.scrollHeight > textInput.current.clientHeight;
        if (scrollHeight < 36 || !hasScrollBar) {
            setTextInputHeight(36);
        } else {
            if (window.innerWidth > 768 && !props.isFullScreen) {
                if (scrollHeight > 96) {
                    setTextInputHeight(96);
                } else {
                    setTextInputHeight(textInput.current.scrollHeight);
                }
            } else {
                if (scrollHeight > 96) {
                    setTextInputHeight(96);
                } else {
                    setTextInputHeight(textInput.current.scrollHeight);
                }
            }
        }
        if (event.key === "Enter" && (event.ctrlKey || isMobileView())) {
            textInput.current.scrollTop = textInput.current.scrollHeight;
        }
    }
    function sendMessage() {
        if (props.isLoading || !props.isConnected || !props.isEnabled) {
            return false;
        }
        if (props.isConnected) {
            props.sendUserMessageCb();
            textInput.current.focus();
        }
        if (window.innerWidth < 768) {
            textInput.current.blur();
        }
    }
    function handleAttachButtonClick(event) {
        fileInput.current.click();
    }

    function handleFileSelected(event) {
        if (!event.target || !event.target.files || !event.target.files.length) {
            return;
        }
        const file = event.target.files[0];
        props.newMultimediaMessageCb(file);
    }
    function toggleEmojiPicker() {
        setShowEmojis(!showEmojis);
    }
    function onEmojiClick(icon, emojiObject) {
        props.handleTextInputCb(textInput.current.value + icon.native);
        setShowEmojis(false);
    }
    function secondsToHms(seconds) {
        if (!seconds) return "0s";

        let duration = seconds;
        let hours = duration / 3600;
        duration = duration % 3600;

        let min = parseInt(duration / 60);
        duration = duration % 60;

        let sec = parseInt(duration);

        if (sec < 10) {
            sec = `0${sec}`;
        }
        if (min < 10) {
            min = `0${min}`;
        }

        if (parseInt(hours, 10) > 0) {
            return `${parseInt(hours, 10)}h ${min}m ${sec}s`;
        } else if (min === 0) {
            return `${sec}s`;
        } else {
            return `${min}m ${sec}s`;
        }
    }
    function sendAudio() {
        if (props.isLoading || !props.isConnected || !props.isEnabled) {
            return false;
        }
        props.changeAudioRecordStateCb(CTE.AUDIO_RECORD_STATES.PENDING);
    }

    useEffect(() => {
        if (props.audioRecordState === CTE.AUDIO_RECORD_STATES.RECORDING) {
            counterInterval = setInterval(() => {
                setRecordCounter((prevState) => prevState + 1);
            }, 1000);
        } else {
            clearInterval(counterInterval);
            setRecordCounter(0);
        }
    }, [props.audioRecordState]);

    useEffect(() => {
        if (props.textInput.length === 0) {
            setTextInputHeight(defaultTextInputHeight);
        }
    }, [props.textInput]);

    return (
        <div
            className={`chat-bar ${
                props.isFullScreen ? "chat-bar--fullscreen" : "chat-bar--embedded"
            }`}
        >
            <WrapInProgress showProgess={inputPlaceholder.status === "err"}>
                {(props.audioRecordState === CTE.AUDIO_RECORD_STATES.RECORDING ||
                    props.audioRecordState === CTE.AUDIO_RECORD_STATES.PENDING) && (
                    <>
                        <div className="chat-bar__container">
                            <div className="chat-bar__container-input">
                                <textarea
                                    className="chat-bar__input"
                                    type="text"
                                    placeholder={"Audio"}
                                    value={""}
                                    onKeyDown={(event) =>
                                        event.key === "Enter" && event.preventDefault()
                                    }
                                    disabled={true}
                                    style={{ height: textInputHeight + "px" }}
                                />
                            </div>
                        </div>
                        {props.audioRecordState === CTE.AUDIO_RECORD_STATES.PENDING && (
                            <div
                                className="chat-bar__icon chat-bar__icon--send chat-bar__icon--send-cancel"
                                style={props.customStyles && props.customStyles.sendButton}
                                onClick={() =>
                                    props.changeAudioRecordStateCb(CTE.AUDIO_RECORD_STATES.DISABLED)
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faHourglassStart}
                                    style={{ color: "white", fontSize: "16px" }}
                                />
                            </div>
                        )}
                        {props.audioRecordState === CTE.AUDIO_RECORD_STATES.RECORDING && (
                            <>
                                <div
                                    className="chat-bar__icon chat-bar__icon--send chat-bar__icon--send-cancel"
                                    style={props.customStyles && props.customStyles.sendButton}
                                    onClick={() =>
                                        props.changeAudioRecordStateCb(
                                            CTE.AUDIO_RECORD_STATES.CANCEL
                                        )
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        style={{ color: "white", fontSize: "16px" }}
                                    />
                                </div>
                                <div className="chat-bar__counter">
                                    <span className="chat-bar__counter-dot"></span>
                                    <span className="chat-bar__counter-time">
                                        {secondsToHms(recordCounter)}
                                    </span>
                                </div>
                                <div
                                    className="chat-bar__icon chat-bar__icon--send chat-bar__icon--send-audio"
                                    style={props.customStyles && props.customStyles.sendButton}
                                    onClick={props.finishRecordingAudioCb}
                                >
                                    <FontAwesomeIcon
                                        icon={faCheck}
                                        style={{ color: "white", fontSize: "16px" }}
                                    />
                                </div>
                            </>
                        )}
                    </>
                )}
                {props.audioRecordState === CTE.AUDIO_RECORD_STATES.BLOCKED && (
                    <>
                        <div className="chat-bar__container">
                            <div className="chat-bar__container-input">
                                <textarea
                                    className="chat-bar__input"
                                    type="text"
                                    placeholder={"Audio Permission dennied"}
                                    value={""}
                                    onKeyDown={(event) =>
                                        event.key === "Enter" && event.preventDefault()
                                    }
                                    disabled={true}
                                    style={{ height: textInputHeight + "px" }}
                                />
                            </div>
                        </div>
                        <div
                            className="chat-bar__icon chat-bar__icon--send"
                            style={props.customStyles && props.customStyles.sendButton}
                            onClick={() =>
                                props.changeAudioRecordStateCb(CTE.AUDIO_RECORD_STATES.DISABLED)
                            }
                        >
                            <FontAwesomeIcon
                                icon={faTimes}
                                style={{ color: "white", fontSize: "16px" }}
                            />
                        </div>
                    </>
                )}
                {(props.audioRecordState === CTE.AUDIO_RECORD_STATES.DISABLED ||
                    !props.showAudioRecordButton) && (
                    <>
                        <div className="chat-bar__container">
                            <div className="chat-bar__container-upload">
                                <input
                                    type="file"
                                    style={{ display: "none" }}
                                    ref={fileInput}
                                    onChange={handleFileSelected}
                                    disabled={!props.isConnected || !props.isEnabled}
                                />
                                <button
                                    className="chat-bar__input-upload"
                                    onClick={handleAttachButtonClick}
                                    disabled={
                                        !props.isConnected ||
                                        !props.isEnabled ||
                                        !props.enableSendMultimediaButton
                                    }
                                >
                                    +
                                </button>
                            </div>
                            <div className="chat-bar__container-input">
                                <textarea
                                    ref={textInput}
                                    className="chat-bar__input"
                                    type="text"
                                    placeholder={inputPlaceholder.placeholder}
                                    value={props.textInput}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                            event.preventDefault();
                                            if (event.ctrlKey || isMobileView()) {
                                                handleTextInput(props.textInput + "\n");
                                            }
                                        }
                                    }}
                                    onKeyUp={handleInputKeyPress}
                                    onChange={(e) => handleTextInput(e.target.value)}
                                    disabled={!props.isConnected || !props.isEnabled}
                                    style={{ height: textInputHeight + "px" }}
                                />
                                <FontAwesomeIcon
                                    icon={faSmile}
                                    className="chat-bar__emoji-icon"
                                    onClick={toggleEmojiPicker}
                                />
                                <div
                                    className="chat-bar__emoji-picker"
                                    style={{ display: showEmojis ? "block" : "none" }}
                                >
                                    <Picker exclude={emojiCategories} onSelect={onEmojiClick} />
                                </div>
                            </div>
                        </div>
                        {props.showAudioRecordButton && !Boolean(props.textInput) ? (
                            <div
                                className="chat-bar__icon chat-bar__icon--send"
                                style={props.customStyles && props.customStyles.sendButton}
                                onClick={() => sendAudio()}
                            >
                                <FontAwesomeIcon
                                    icon={faMicrophone}
                                    style={{
                                        color: "white",
                                        fontSize: "14px",
                                        minWidth: "15px",
                                    }}
                                />
                            </div>
                        ) : (
                            <div
                                className="chat-bar__icon chat-bar__icon--send"
                                style={props.customStyles && props.customStyles.sendButton}
                            >
                                <img onClick={sendMessage} src={props.sendIcon} alt="send" />
                            </div>
                        )}
                    </>
                )}
            </WrapInProgress>
        </div>
    );
}

export default SendMessages;
